import { LEAD_SEARCH_WINDOW_DAYS } from '../../../../pages/MessengerV2/MessageSearch.constants';
import {
  isNegationOnlyQuery,
  isSearchTermLongEnough,
} from '../../../../pages/MessengerV2/MessageSearch.functions';
import { tSearchLeadMessagesResponse } from '../../../../pages/MessengerV2/MessageSearch.types';
import { tChatSearchStatus } from './ChatSearch.types';

/** The window in months, for the copy ("últimos 3 meses"). One source. */
export const LEAD_SEARCH_WINDOW_MONTHS = Math.round(
  LEAD_SEARCH_WINDOW_DAYS / 30,
);

/** Can this trimmed term be sent at all? See isNegationOnlyQuery. */
export function canSearchTerm(term: string): boolean {
  return isSearchTermLongEnough(term) && !isNegationOnlyQuery(term);
}

/**
 * The paging state one response leaves behind.
 *
 * The cursor is opaque and arrives as `""` when there is no next page, so an
 * empty cursor is "no more" whatever `hasMore` says; `partial` means nothing
 * without a page to continue to.
 *
 * @param page - The response body.
 * @returns The cursor to send next, and whether more (and partial) remains.
 */
export function readPagingState(page: Partial<tSearchLeadMessagesResponse>): {
  cursor: string;
  hasMore: boolean;
  partial: boolean;
} {
  const cursor = page.hasMore ? (page.cursor ?? '') : '';
  const hasMore = Boolean(cursor);

  return { cursor, hasMore, partial: hasMore && Boolean(page.partial) };
}

/**
 * What Enter / ∧ does from the current position.
 *
 * 'goto'  — an older match is already loaded: step to it (no request).
 * 'fetch' — this is the oldest loaded match and the server has more. On a full
 *           page that is the automatic "Buscando más…"; on a partial one it is
 *           the same "Seguir buscando" the button offers, asked for from the
 *           keyboard — never fired without the user asking.
 * 'end'   — the oldest match in the window: nothing to do.
 * 'busy'  — a request is in flight; a second one would race it.
 */
export type tOlderStep =
  | { kind: 'goto'; index: number }
  | { kind: 'fetch' }
  | { kind: 'end' }
  | { kind: 'busy' };

export function resolveOlderStep(state: {
  activeIndex: number;
  matchCount: number;
  hasMore: boolean;
  busy: boolean;
}): tOlderStep {
  const { activeIndex, matchCount, hasMore, busy } = state;

  if (busy) return { kind: 'busy' };

  if (activeIndex < matchCount - 1)
    return { kind: 'goto', index: activeIndex + 1 };

  return hasMore ? { kind: 'fetch' } : { kind: 'end' };
}

/**
 * The bar's state, from the text as typed (so the hints never wait for the
 * debounce) and the state of the search the debounced text started.
 */
export function deriveChatSearchStatus(state: {
  isOpen: boolean;
  typedTerm: string;
  searchTerm: string;
  loading: boolean;
  failed: boolean;
  matchCount: number;
}): tChatSearchStatus {
  const { isOpen, typedTerm, searchTerm, loading, failed, matchCount } = state;

  if (!isOpen || !typedTerm) return 'idle';
  if (!isSearchTermLongEnough(typedTerm)) return 'hint';
  if (isNegationOnlyQuery(typedTerm)) return 'negation';
  // Typed but not settled yet: the answer on screen is for another text.
  if (typedTerm !== searchTerm || loading) return 'loading';
  if (matchCount) return 'results';

  return failed ? 'error' : 'empty';
}

const ENTITIES: Record<string, string> = {
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#39;': "'",
};

/**
 * The words the server marked in `ts_headline` snippets, deduplicated
 * case-insensitively, longest first (so a regex alternation built from them
 * prefers "facturas" over "factura").
 *
 * Read from the `<b>` tags rather than re-derived from the query: Postgres
 * matched with Spanish stemming and unaccent, so "facturas" and "Envío" are
 * what it found for "factura envio", and only the snippet says so.
 *
 * @param snippets - One snippet per loaded match.
 * @returns The distinct matched words.
 */
export function extractSnippetTerms(snippets: string[]): string[] {
  const terms = new Map<string, string>();

  for (const snippet of snippets) {
    for (const match of (snippet ?? '').matchAll(/<b>([\s\S]*?)<\/b>/gi)) {
      const term = match[1]
        .replace(/&(amp|lt|gt|quot|#39);/g, (entity) => ENTITIES[entity])
        .trim();

      if (!term) continue;

      const key = term.toLocaleLowerCase();

      if (!terms.has(key)) terms.set(key, term);
    }
  }

  return Array.from(terms.values()).sort((a, b) => b.length - a.length);
}
